import { computed, defineComponent, h, inject, PropType, VNode } from 'vue'

import { DEFAULT_CLASS_PREFIX, injectCarousel } from '@/shared'
import {
  getNumberInRange,
  getSnapAlignOffset,
  i18nFormatter,
  mapNumberToRange,
} from '@/utils'

import { PaginationProps } from './Pagination.types'

export const Pagination = defineComponent<PaginationProps>({
  name: 'CarouselPagination',
  props: {
    disableOnClick: {
      type: Boolean,
    },
    paginateByItemsToShow: {
      type: Boolean,
    },
    carousel: {
      type: Object as PropType<PaginationProps['carousel']>,
    }
  },
  setup(props) {
    let carousel = inject(injectCarousel, null)!

    const itemsToShow = computed(() => carousel.config.itemsToShow as number)
    const offset = computed(() =>
      getSnapAlignOffset({
        align: carousel.config.snapAlign,
        itemsToShow: itemsToShow.value,
      })
    )
    const isPaginated = computed(
      () => props.paginateByItemsToShow && itemsToShow.value > 1
    )
    const currentPage = computed(() =>
      Math.ceil((carousel.activeSlide - offset.value) / itemsToShow.value)
    )
    const pageCount = computed(() => Math.ceil(carousel.slidesCount / itemsToShow.value))

    // Without wrapAround the last step lands on the last slide, which can give a
    // page past the end; clamp it there instead of wrapping to the first page.
    const isActive = (slide: number): boolean =>
      (carousel.config.wrapAround ? mapNumberToRange : getNumberInRange)(
        isPaginated.value
          ? {
              val: currentPage.value,
              max: pageCount.value - 1,
              min: 0,
            }
          : {
              val: carousel.activeSlide,
              max: carousel.maxSlide,
              min: carousel.minSlide,
            }
      ) === slide

    // Without wrapAround the last page targets the last slide, where next() also
    // lands, so the Navigation button disables there too.
    const getPageTarget = (slide: number): number => {
      if (!isPaginated.value) {
        return slide
      }
      const index = Math.floor(slide * itemsToShow.value + offset.value)
      const reachesEnd =
        !carousel.config.wrapAround &&
        itemsToShow.value <= carousel.slidesCount &&
        index - offset.value >= carousel.slidesCount - itemsToShow.value
      return reachesEnd ? carousel.maxSlide : index
    }

    return () => {
      if (props.carousel) {
        carousel = props.carousel;
      }
      if (!carousel) {
        console.warn('[vue3-carousel]: A carousel component must be provided for the pagination component to display')
        return '';
      }
      if (carousel.isMarquee) {
        return ''
      }
      if (carousel.isLocked) {
        return ''
      }
      const prefix = carousel.config.classPrefix || DEFAULT_CLASS_PREFIX
      const children: Array<VNode> = []

      for (
        let slide = isPaginated.value ? 0 : carousel.minSlide;
        slide <= (isPaginated.value ? pageCount.value - 1 : carousel.maxSlide);
        slide++
      ) {
        const buttonLabel = i18nFormatter(
          carousel.config.i18n[
            isPaginated.value ? 'ariaNavigateToPage' : 'ariaNavigateToSlide'
          ],
          {
            slideNumber: slide + 1,
          }
        )
        const active = isActive(slide)
        const button = h('button', {
          type: 'button',
          class: {
            [`${prefix}__pagination-button`]: true,
            [`${prefix}__pagination-button--active`]: active,
          },
          'aria-label': buttonLabel,
          'aria-pressed': active,
          'aria-controls': carousel.slides[slide]?.exposed?.id,
          title: buttonLabel,
          disabled: props.disableOnClick,
          onClick: () => carousel.nav.slideTo(getPageTarget(slide)),
        })
        const item = h('li', { class: `${prefix}__pagination-item`, key: slide }, button)
        children.push(item)
      }

      return h('ol', { class: `${prefix}__pagination` }, children)
    }
  },
})
